import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import { SaveIcon, HomeIcon, CogIcon } from '@heroicons/react/outline'
import Loader from '../UI/Loader'
import BannerInfo from '../UI/BannerInfo'
import OnlyPremiumEmpty from '../UI/OnlyPremiumEmpty'
import PremiumAlert from '../UI/PremiumAlert'
import LangSelector from '../UI/LangSelector'
import fr_assets from '../../langs/main.fr.json'
import en_assets from '../../langs/main.en.json'
import de_assets from '../../langs/main.de.json'
import nl_assets from '../../langs/main.nl.json'
import it_assets from '../../langs/main.it.json'
import es_assets from '../../langs/main.es.json'
import pt_assets from '../../langs/main.pt.json'

// Textes par défaut des réponses auto, par langue (repli FR)
const LANG_DEFAULTS = { fr: fr_assets, en: en_assets, de: de_assets, nl: nl_assets, it: it_assets, es: es_assets, pt: pt_assets }

const DEFAULT_CONFIG = {
	auto_reply_enabled: false,
	availability_start: 8,
	availability_end: 20,
	reply_inside: [],
	reply_outside: [],
}

const BellIcon = ({ className }) => (
	<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
		<path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
	</svg>
)

const ClockIcon = ({ className }) => (
	<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
		<path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
	</svg>
)

class MessagerieParam extends Component
{
	constructor(props)
	{
		super(props)
		this.state = {
			form: { ...DEFAULT_CONFIG },
			current_lang: props.lang_default || 'fr',
			loading: true,
			saving: false,
			banner_content: null,
			show_banner: false
		}
		this.props.updateNavbarTitle('Messagerie — Paramètres')
	}

	componentDidMount()
	{
		this.props.fetchConfigMessages()
		.then(config => {
			if (config && typeof config === 'object')
			{
				const avail_start = Number(config.availability_start)
				const avail_end = Number(config.availability_end)
				this.setState({
					form: {
						auto_reply_enabled: config.auto_reply_enabled === true,
						availability_start: (!isNaN(avail_start) && avail_start >= 0 && avail_start <= 23) ? avail_start : 8,
						availability_end: (!isNaN(avail_end) && avail_end >= 0 && avail_end <= 23) ? avail_end : 20,
						reply_inside: this._normalizeReplies(config.reply_inside),
						reply_outside: this._normalizeReplies(config.reply_outside),
					},
					loading: false
				})
			}
			else
				this.setState({ loading: false })
		})
		.catch(() => {
			this.setState({ loading: false })
		})
	}

	_toggle = (field) => {
		this.setState(prev => ({
			form: { ...prev.form, [field]: !prev.form[field] }
		}))
	}

	_setHour = (field, raw) => {
		if (raw === '')
		{
			this.setState(prev => ({ form: { ...prev.form, [field]: '' } }))
			return
		}
		const v = parseInt(raw, 10)
		if (!isNaN(v) && v >= 0 && v <= 23)
			this.setState(prev => ({ form: { ...prev.form, [field]: v } }))
	}

	_setField = (field, value) => {
		this.setState(prev => ({ form: { ...prev.form, [field]: value } }))
	}

	updateLang = (lang) => {
		this.setState({ current_lang: lang })
	}

	// Texte par défaut (placeholder / aperçu) dans la langue d'édition courante
	_defaultReply = (key) => {
		const assets = LANG_DEFAULTS[this.state.current_lang] || LANG_DEFAULTS.fr
		if (assets && assets.messages && assets.messages[key])
			return assets.messages[key]
		return LANG_DEFAULTS.fr.messages[key]
	}

	// Convertit la valeur stockée en tableau de traductions [{ lang, text }].
	// Gère l'ancien format (chaîne unique) en l'attribuant à la langue par défaut.
	_normalizeReplies = (value) => {
		if (Array.isArray(value))
			return value.filter(t => t && t.lang).map(t => ({ lang: t.lang, text: t.text || '' }))
		if (typeof value === 'string' && value.length > 0)
			return [{ lang: this.props.lang_default || 'fr', text: value }]
		return []
	}

	_getTranslationText = (translations, lang) => {
		if (!Array.isArray(translations))
			return ''
		const entry = translations.find(t => t.lang === lang)
		return entry ? entry.text : ''
	}

	_setTranslationText = (translations, lang, value) => {
		const arr = Array.isArray(translations) ? [...translations] : []
		const idx = arr.findIndex(t => t.lang === lang)
		if (idx >= 0)
			arr[idx] = { lang, text: value }
		else
			arr.push({ lang, text: value })
		return arr
	}

	_save = () => {
		const { form } = this.state
		const avail_start = parseInt(form.availability_start, 10)
		const avail_end = parseInt(form.availability_end, 10)
		if (isNaN(avail_start) || avail_start < 0 || avail_start > 23 ||
			isNaN(avail_end) || avail_end < 0 || avail_end > 23)
		{
			this.setState({ show_banner: true, banner_content: "Les horaires doivent être des heures valides (0–23)." })
			return
		}
		if (avail_start >= avail_end)
		{
			this.setState({ show_banner: true, banner_content: "L'heure de début doit être inférieure à l'heure de fin." })
			return
		}
		this.setState({ saving: true })
		this.props.updateConfigMessages({
			auto_reply_enabled: form.auto_reply_enabled,
			availability_start: avail_start,
			availability_end: avail_end,
			reply_inside: form.reply_inside,
			reply_outside: form.reply_outside
		})
		.then(result => {
			if (result === false)
			{
				this.setState({ saving: false, show_banner: true, banner_content: "Erreur lors de la sauvegarde." })
				return
			}
			this.setState({ saving: false, show_banner: true, banner_content: "Paramètres enregistrés." })
		})
		.catch(() => {
			this.setState({ saving: false, show_banner: true, banner_content: "Erreur lors de la sauvegarde." })
		})
	}

	_buildPreview = () => {
		const { form, current_lang } = this.state
		return {
			inside: this._getTranslationText(form.reply_inside, current_lang) || this._defaultReply('reply_inside'),
			outside: this._getTranslationText(form.reply_outside, current_lang) || this._defaultReply('reply_outside')
		}
	}

	_renderToggle = (label, description, iconElement, enabled, onToggle) => {
		return (
			<div
				onClick={onToggle}
				className="flex items-start justify-between p-4 rounded-2xl border border-transparent hover:bg-slate-50 cursor-pointer transition-all"
			>
				<div className="flex gap-4">
					{iconElement && (
						<div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${enabled ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-400'}`}>
							{iconElement}
						</div>
					)}
					<div className="flex flex-col justify-center">
						<span className="text-sm font-bold text-slate-900">{label}</span>
						{description && <span className="text-[11px] text-slate-500 font-medium mt-0.5">{description}</span>}
					</div>
				</div>
				<div className={`w-12 h-6 rounded-full relative flex-shrink-0 ml-4 mt-1 transition-all duration-300 ${enabled ? 'bg-indigo-500' : 'bg-slate-200'}`}>
					<div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all duration-300 ${enabled ? 'left-7' : 'left-1'}`} />
				</div>
			</div>
		)
	}

	render()
	{
		if (this.props.features !== 'premium') {
			return (
				<>
					<OnlyPremiumEmpty
						title="Messagerie"
						subtitle="Paramétrez votre messagerie (horaires, message d'absence...)"
						emptyFolder="Pas de paramètres"
						emptyFolderSubtitle="Pas de messagerie"
						navName="Messagerie"
						navPath="/messages/chat"
					/>
					<div className="mt-10">
						<PremiumAlert
							content="Mettez votre abonnement à niveau avec la formule premium pour accéder à la messagerie !"
							button_text="En savoir plus"
						/>
					</div>
				</>
			)
		}

		const { form, loading, saving, current_lang } = this.state
		const preview = this._buildPreview()

		const save_btn = saving
			? <div className="px-6 py-3"><Loader /></div>
			: (
				<button
					onClick={this._save}
					className="px-6 py-3 bg-indigo-600 rounded-2xl text-sm font-bold text-white flex items-center gap-2 shadow-sm hover:bg-indigo-700 transition-colors"
					style={{ backgroundImage: 'none', backgroundColor: '#4f46e5' }}
				>
					<SaveIcon className="h-4 w-4" />
					<span>Enregistrer</span>
				</button>
			)

		return (
			<>
				<BannerInfo
					content={this.state.banner_content}
					show={this.state.show_banner}
					closeBanner={() => this.setState({ show_banner: false })}
				/>

				{/* Breadcrumb */}
				<div className="mb-5" aria-label="Breadcrumb">
					<ol className="flex items-center gap-1 text-sm">
						<li className="flex items-center">
							<Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors">
								<HomeIcon className="flex-shrink-0 h-4 w-4" aria-hidden="true" />
								<span className="sr-only">Accueil</span>
							</Link>
						</li>
						<li className="flex items-center">
							<span className="text-gray-300 mx-1 select-none">/</span>
							<Link to="/messages/chat" className="text-gray-400 hover:text-gray-600 transition-colors">Messagerie</Link>
						</li>
						<li className="flex items-center">
							<span className="text-gray-300 mx-1 select-none">/</span>
							<span className="text-gray-500">Paramètres</span>
						</li>
					</ol>
				</div>

				{/* Header */}
				<div className="flex items-end justify-between mb-8">
					<div>
						<h1 style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: '-0.02em', margin: 0 }} className="text-4xl font-bold text-gray-900 leading-none">
							Paramètres messagerie
						</h1>
						<p className="text-sm text-gray-400" style={{ margin: '8px 0 0 0' }}>Accusé de réception et disponibilités</p>
					</div>
					{save_btn}
				</div>

				{loading ? (
					<div className="flex items-center justify-center h-32">
						<Loader />
					</div>
				) : (
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start max-w-5xl">

						{/* Colonne gauche : activation + horaires */}
						<div className="flex flex-col gap-6">

							{/* Toggle accusé de réception */}
							<div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
								<div className="flex items-center gap-4 mb-6">
									<div className="p-2.5 bg-white border border-slate-100 rounded-2xl shadow-sm text-slate-600">
										<BellIcon className="h-5 w-5" />
									</div>
									<div>
										<h3 className="text-lg font-black text-slate-900 tracking-tight leading-tight" style={{ margin: 0 }}>Accusé de réception</h3>
										<p className="text-xs text-slate-500 font-medium" style={{ margin: 0 }}>Envoi automatique lors du premier message d'un vacancier</p>
									</div>
								</div>
								{this._renderToggle(
									"Activer l'accusé de réception automatique",
									"Un message est envoyé automatiquement au vacancier dès son premier message",
									<BellIcon className="h-5 w-5" />,
									form.auto_reply_enabled,
									() => this._toggle('auto_reply_enabled')
								)}
							</div>

							{/* Plage horaire */}
							<div className={`bg-white rounded-3xl border border-slate-200 shadow-sm p-8 transition-opacity ${form.auto_reply_enabled ? '' : 'opacity-50 pointer-events-none'}`}>
								<div className="flex items-center gap-4 mb-6">
									<div className="p-2.5 bg-white border border-slate-100 rounded-2xl shadow-sm text-slate-600">
										<ClockIcon className="h-5 w-5" />
									</div>
									<div>
										<h3 className="text-lg font-black text-slate-900 tracking-tight leading-tight" style={{ margin: 0 }}>Horaires de disponibilité</h3>
										<p className="text-xs text-slate-500 font-medium" style={{ margin: 0 }}>En dehors de ces horaires, le message précise quand l'équipe est joignable</p>
									</div>
								</div>
								<div className="flex flex-col gap-3">
									{[
										{ key: 'availability_start', label: 'Début de disponibilité' },
										{ key: 'availability_end',   label: 'Fin de disponibilité' }
									].map(({ key, label }) => (
										<div key={key} className="flex items-center justify-between gap-4 px-4 py-1">
											<span className="text-sm font-bold text-slate-900 shrink-0">{label}</span>
											<div className="flex items-center gap-2">
												<input
													type="number"
													min="0"
													max="23"
													value={form[key]}
													onChange={e => this._setHour(key, e.target.value)}
													className="browser-default w-24 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium text-slate-700 text-right focus:outline-none focus:border-indigo-400 focus:bg-white transition-all"
													placeholder="hh"
												/>
												<span className="text-sm text-slate-500 font-medium">h00</span>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>

						{/* Colonne droite : messages automatiques (multilingue) */}
						<div className="flex flex-col gap-6">
							<div className={`bg-white rounded-3xl border border-slate-200 shadow-sm p-8 transition-opacity ${form.auto_reply_enabled ? '' : 'opacity-50 pointer-events-none'}`}>
								<div className="flex items-center gap-4 mb-6">
									<div className="p-2.5 bg-white border border-slate-100 rounded-2xl shadow-sm text-slate-600">
										<CogIcon className="h-5 w-5" />
									</div>
									<div>
										<h3 className="text-lg font-black text-slate-900 tracking-tight leading-tight" style={{ margin: 0 }}>Messages automatiques</h3>
										<p className="text-xs text-slate-500 font-medium" style={{ margin: 0 }}>Personnalisez les réponses envoyées selon l'heure du message, dans la langue du vacancier</p>
									</div>
								</div>

								{/* Sélecteur de langue */}
								<div className="mb-6">
									<span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider" style={{ margin: '0 0 10px 0' }}>Langue d'édition</span>
									<LangSelector variant="card" lang={current_lang} updateLang={this.updateLang} />
								</div>

								<div className="flex flex-col" style={{ gap: 24 }}>
									{/* Message dans les horaires */}
									<div className="bg-slate-50 rounded-2xl" style={{ padding: 16 }}>
										<p className="text-[10px] font-black text-slate-400 uppercase tracking-wider" style={{ margin: '0 0 12px 0' }}>
											Dans les horaires ({parseInt(form.availability_start, 10)}h – {parseInt(form.availability_end, 10)}h)
										</p>
										<textarea
											value={this._getTranslationText(form.reply_inside, current_lang)}
											onChange={e => this._setField('reply_inside', this._setTranslationText(form.reply_inside, current_lang, e.target.value))}
											placeholder={this._defaultReply('reply_inside')}
											rows={3}
											className="browser-default"
											style={{ width: '100%', padding: '12px 16px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, fontSize: 14, fontWeight: 500, color: '#334155', resize: 'none', outline: 'none', boxSizing: 'border-box', margin: 0 }}
										/>
										<div className="flex justify-end" style={{ marginTop: 12 }}>
											<div className="bg-indigo-600 text-white text-sm rounded-2xl rounded-tr-none max-w-xs shadow-sm" style={{ padding: '12px 16px' }}>
												{preview.inside}
											</div>
										</div>
										<p className="text-right text-xs text-slate-400 font-bold uppercase" style={{ margin: '4px 0 0 0' }}>Aperçu</p>
									</div>
									{/* Message hors horaires */}
									<div className="bg-slate-50 rounded-2xl" style={{ padding: 16 }}>
										<p className="text-[10px] font-black text-slate-400 uppercase tracking-wider" style={{ margin: '0 0 12px 0' }}>
											Hors horaires
										</p>
										<textarea
											value={this._getTranslationText(form.reply_outside, current_lang)}
											onChange={e => this._setField('reply_outside', this._setTranslationText(form.reply_outside, current_lang, e.target.value))}
											placeholder={this._defaultReply('reply_outside')}
											rows={3}
											className="browser-default"
											style={{ width: '100%', padding: '12px 16px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, fontSize: 14, fontWeight: 500, color: '#334155', resize: 'none', outline: 'none', boxSizing: 'border-box', margin: 0 }}
										/>
										<div className="flex justify-end" style={{ marginTop: 12 }}>
											<div className="bg-indigo-600 text-white text-sm rounded-2xl rounded-tr-none max-w-xs shadow-sm" style={{ padding: '12px 16px' }}>
												{preview.outside}
											</div>
										</div>
										<p className="text-right text-xs text-slate-400 font-bold uppercase" style={{ margin: '4px 0 0 0' }}>Aperçu</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</>
		)
	}
}

export default MessagerieParam
